import React from "react";
import {
  Briefcase,
  Plus,
  MoreVertical,
  CheckCircle,
  XCircle,
  Clock,
  Loader2,
} from "lucide-react";
import { UserAssignment } from "../../../lib/types/adminProjects";

interface UserAssignmentsListProps {
  assignments: UserAssignment[];
  isLoading: boolean;
  isUserActive: boolean;
  statusFilter: string;
  onStatusFilterChange: (value: string) => void;
  onAssignProject: () => void;
  onOpenMenu: (
    assignmentId: string,
    e: React.MouseEvent<HTMLButtonElement>
  ) => void;
  formatDate: (dateString?: string) => string;
}

const getStatusConfig = (status: string) => {
  const configs: Record<string, { bg: string; text: string; icon: any }> = {
    assigned: { bg: "bg-blue-50", text: "text-blue-700", icon: Clock },
    pending: { bg: "bg-amber-50", text: "text-amber-700", icon: Clock },
    approved: {
      bg: "bg-emerald-50",
      text: "text-emerald-700",
      icon: CheckCircle,
    },
    rejected: { bg: "bg-red-50", text: "text-red-700", icon: XCircle },
    active: { bg: "bg-green-50", text: "text-green-700", icon: CheckCircle },
    completed: {
      bg: "bg-purple-50",
      text: "text-purple-700",
      icon: CheckCircle,
    },
    terminated: { bg: "bg-gray-100", text: "text-gray-600", icon: XCircle },
  };
  return (
    configs[status] || {
      bg: "bg-gray-100",
      text: "text-gray-600",
      icon: Clock,
    }
  );
};

const UserAssignmentsList: React.FC<UserAssignmentsListProps> = ({
  assignments,
  isLoading,
  isUserActive,
  statusFilter,
  onStatusFilterChange,
  onAssignProject,
  onOpenMenu,
  formatDate,
}) => {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
      {/* Header */}
      <div className="p-4 sm:p-6 border-b border-gray-100">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#8B52FF] to-[#E05A6A] flex items-center justify-center">
              <Briefcase className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Assigned Projects
              </h2>
              <p className="text-sm text-gray-500">
                {assignments.length} total assignments
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={statusFilter}
              onChange={(e) => onStatusFilterChange(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#8B52FF]/20 focus:border-[#8B52FF] bg-white"
            >
              <option value="">All Statuses</option>
              <option value="assigned">Assigned</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="rejected">Rejected</option>
            </select>
            <button
              onClick={onAssignProject}
              disabled={!isUserActive}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] text-white font-semibold rounded-xl shadow-lg shadow-purple-500/25 hover:shadow-xl transition-all text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="w-4 h-4" />
              Assign Project
            </button>
          </div>
        </div>
      </div>

      {/* Assignments List */}
      <div className="divide-y divide-gray-50">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[#8B52FF]" />
          </div>
        ) : assignments.length === 0 ? (
          <div className="text-center py-12">
            <Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              No assignments
            </h3>
            <p className="text-gray-500 mb-4">
              Assign a project to this user to get started
            </p>
            {isUserActive && (
              <button
                onClick={onAssignProject}
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#8B52FF] text-white rounded-lg"
              >
                <Plus className="w-4 h-4" />
                Assign Project
              </button>
            )}
          </div>
        ) : (
          assignments.map((assignment) => {
            const statusConfig = getStatusConfig(assignment.status);
            const StatusIcon = statusConfig.icon;

            return (
              <div
                key={assignment._id}
                className="p-4 sm:p-6 hover:bg-gray-50/50 transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-gray-900">
                        {assignment.projectId.title}
                      </h3>
                      <span
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${statusConfig.bg} ${statusConfig.text}`}
                      >
                        <StatusIcon className="w-3.5 h-3.5" />
                        {assignment.status.charAt(0).toUpperCase() +
                          assignment.status.slice(1)}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                      <span>{assignment.projectId.category}</span>
                      <span>•</span>
                      <span>${assignment.projectId.hourlyRate}/hr</span>
                      <span>•</span>
                      <span>Assigned {formatDate(assignment.assignedAt)}</span>
                    </div>
                  </div>

                  {/* Actions Button */}
                  <button
                    onClick={(e) => onOpenMenu(assignment._id, e)}
                    className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <MoreVertical className="w-4 h-4 text-gray-500" />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default UserAssignmentsList;
